/**
 * StandingChip (TMMPDM-0022) — a reseller's minimum-commitment state in words, through the one
 * `StatusChip` (TMMPDM-0065): GOOD → success; PROBATION and DEACTIVATION_DUE → warning;
 * DEACTIVATED → neutral. The tone is the registry's (`src/status/tones.ts`), the label is ours.
 *
 * TMMPDM-0024: a state this console doesn't know renders neutrally (outlined, the raw
 * value in "Unknown (…)") instead of throwing — a new server state must not take down a list.
 */
import { useCopy } from '@/hooks/useCopy';
import { StatusChip } from '@/status';
import type { ResellerStandingState } from '@/api/tmm/types';

const STANDING_FALLBACK: Record<ResellerStandingState, string> = {
  GOOD: 'Good',
  PROBATION: 'Probation',
  DEACTIVATION_DUE: 'Deactivation due',
  DEACTIVATED: 'Deactivated',
};

function isKnownState(state: string): state is ResellerStandingState {
  return Object.hasOwn(STANDING_FALLBACK, state);
}

interface StandingChipProps {
  state: string;
}

export function StandingChip({ state }: StandingChipProps) {
  const { t } = useCopy();
  if (!isKnownState(state)) {
    return <StatusChip label={t('resellers.standing.unknown', 'Unknown ({{state}})', { state })} />;
  }
  return (
    <StatusChip status={state} label={t(`resellers.standing.${state}`, STANDING_FALLBACK[state])} />
  );
}
